import React, { useEffect } from 'react';
import { NumberField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { Yearfield as YearfieldType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { ElementValue } from '../../types/form-values';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getMinMaxLength } from '../../helpers/getMinMaxLength';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';

const Yearfield = ({ elementId, caption, placeholder, helpText }: YearfieldType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const [errorMessage, setErrorMessage] = React.useState('');
    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const value = Yearfield.getValueOrDefault(formValues, elementId);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Yearfield.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);
    const { minLength, maxLength } = getMinMaxLength(formConfig, elementId);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <NumberField
                value={value}
                onValueChange={(values) => {
                    const value = values.value;
                    setFormValue(elementId, value);
                }}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                isValid={isValid}
                id={elementId}
                key={elementId}
                errorText={errorMessage}
                label={placeholder}
                helpText={helpText}
                required={isRequiredField}
                aria-label={caption}
                data-testid="yearfield"
                minLength={minLength}
                maxLength={maxLength}
            />
        </>
    );
};

Yearfield.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as YearfieldType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

Yearfield.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Yearfield.getValue(formValues, elementId);
    const valueStr = Yearfield.getValueHuman(formValues, elementId);

    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('yearfield', elementId, label, valueStr, valueId);
};

Yearfield.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Yearfield.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Yearfield.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Yearfield.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = Yearfield.getValue(formValues, elementId);

    return value;
};

Yearfield.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean | undefined => {
    return getIsValid(formConfig, formValues, elementId);
};

Yearfield.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    // const value = Yearfield.getValue(formValues, elementId);

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    // if (value === '' || value === undefined) {
    //     return undefined;
    // }

    const isValid = Yearfield.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Yearfield;
